import React, { useEffect, useState } from "react";
import { ServiceCategory, ServiceCategoryGroup } from "../../../../types/types";
import { ServiceFormValue } from "../serviceFormTypes";
interface Props {
  value: ServiceFormValue;
  categories: ServiceCategory[];
  groups: ServiceCategoryGroup[];
  onChange: (value: ServiceFormValue) => void;
  onCategoryChange: (id: number | null) => void;
}
export function ServiceClassificationFields({
  value,
  categories,
  groups,
  onChange,
  onCategoryChange
}: Props) {
  const category = categories.find(c => c.serviceCategoryId === value.serviceCategoryId);
  const [groupId, setGroupId] = useState<number | null>(category?.categoryGroupId ?? null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");
  useEffect(() => {
    if (category?.categoryGroupId) setGroupId(category.categoryGroupId);
  }, [category?.categoryGroupId]);
  const availableGroups = groups.filter(g => g.isActive || g.categoryGroupId === groupId).sort((a, b) => a.displayOrder - b.displayOrder);
  const activeCategory = (c: ServiceCategory) => c.isActive !== false && groups.some(g => g.categoryGroupId === c.categoryGroupId && g.isActive);
  const allSubcategories = categories.flatMap(c => c.subcategories.map(s => ({
    ...s,
    category: c
  })));
  const primaryOptions = category?.subcategories.filter(s => (activeCategory(category) && s.isActive !== false) || value.serviceSubcategoryIds.includes(s.serviceSubcategoryId)) ?? [];
  const updatePrimary = (id: number | null) => {
    onChange({
      ...value,
      primaryServiceSubcategoryId: id,
      serviceSubcategoryIds: id && !value.serviceSubcategoryIds.includes(id) ? [...value.serviceSubcategoryIds, id] : value.serviceSubcategoryIds
    });
  };
  const searchTerm = search.trim().toLocaleLowerCase();
  const matchingCategories = categories.map(c => ({
    category: c,
    subcategories: c.subcategories.filter(s =>
      ((activeCategory(c) && s.isActive !== false) || value.serviceSubcategoryIds.includes(s.serviceSubcategoryId)) &&
      (!searchTerm || `${c.name} ${s.name}`.toLocaleLowerCase().includes(searchTerm)))
  })).filter(c => c.subcategories.length > 0);
  const remove = (id: number) => onChange({
    ...value,
    serviceSubcategoryIds: value.serviceSubcategoryIds.filter(x => x !== id),
    primaryServiceSubcategoryId: value.primaryServiceSubcategoryId === id ? null : value.primaryServiceSubcategoryId
  });
  if (!groups.length) return <div role="status" className="field-hint">The classification catalogue is unavailable. Refresh reference data or check the API connection.</div>;
  return <>
  <div className="form-group">
   <label className="form-label" htmlFor="service-category-group">Main category group *</label>
   <select id="service-category-group" className="form-input" value={groupId ?? ""} onChange={e => {
        const id = e.target.value ? Number(e.target.value) : null;
        setGroupId(id);
        if (id !== groupId) onCategoryChange(null);
      }}>
    <option value="">Select a group</option>
    {availableGroups.map(g => <option key={g.categoryGroupId} value={g.categoryGroupId} disabled={!g.isActive}>{g.name}{!g.isActive ? " (inactive)" : ""}</option>)}
   </select>
  </div>
  <div className="form-group">
   <label className="form-label" htmlFor="service-category">Service category *</label>
   <select id="service-category" className="form-input" value={value.serviceCategoryId ?? ""} disabled={!groupId} onChange={e => onCategoryChange(e.target.value ? Number(e.target.value) : null)}>
    <option value="">Select a category</option>
    {categories.filter(c => c.categoryGroupId === groupId && (activeCategory(c) || c.serviceCategoryId === value.serviceCategoryId)).map(c => <option key={c.serviceCategoryId} value={c.serviceCategoryId} disabled={!activeCategory(c)}>{c.name}{!activeCategory(c) ? " (inactive)" : ""}</option>)}
   </select>
  </div>
  <div className="form-group">
   <label className="form-label" htmlFor="service-primary-subcategory">Primary subcategory *</label>
   <select id="service-primary-subcategory" className="form-input" value={value.primaryServiceSubcategoryId ?? ""} disabled={!category} onChange={e => updatePrimary(e.target.value ? Number(e.target.value) : null)}>
    <option value="">Select a primary subcategory</option>
    {primaryOptions.map(s => <option key={s.serviceSubcategoryId} value={s.serviceSubcategoryId} disabled={s.isActive === false && !value.serviceSubcategoryIds.includes(s.serviceSubcategoryId)}>{s.name}{s.isActive === false ? " (inactive)" : ""}</option>)}
   </select>
   {category && !primaryOptions.length && <div className="field-hint">No subcategories are configured for this category.</div>}
   {value.serviceSubcategoryIds.length > 0 && !value.primaryServiceSubcategoryId && <div role="alert" className="field-hint">Choose a primary subcategory in the main category.</div>}
  </div>
  <div className="form-group">
   <button type="button" className="service-classification-picker-toggle" aria-expanded={pickerOpen}
     aria-controls="additional-classification-picker" onClick={() => setPickerOpen(open => !open)}>
     <span aria-hidden="true">{pickerOpen ? "✓" : "+"}</span>
     {pickerOpen ? "Done" : "Add classification"}
   </button>
   {pickerOpen && <div id="additional-classification-picker" className="service-classification-picker">
     <label className="form-label" htmlFor="classification-search">Search categories or subcategories</label>
     <input id="classification-search" type="search" className="form-input" value={search}
       onChange={e => setSearch(e.target.value)} autoFocus
       onKeyDown={e => { if (e.key === "Escape") { e.preventDefault(); setPickerOpen(false); } }}
       placeholder="e.g. sport, music or holiday clubs" />
     <div className="service-classification-picker-results">
       {!matchingCategories.length && <p role="status" className="field-hint">No matching classifications.</p>}
       {matchingCategories.map(({ category: parent, subcategories }) => <fieldset key={parent.serviceCategoryId}
         className="service-classification-picker-group">
         <legend>{parent.name}</legend>
         {subcategories.map(sub => {
           const selected = value.serviceSubcategoryIds.includes(sub.serviceSubcategoryId);
           const primary = value.primaryServiceSubcategoryId === sub.serviceSubcategoryId;
           const inactive = !activeCategory(parent) || sub.isActive === false;
           return <label key={sub.serviceSubcategoryId} className="service-classification-picker-option">
             <input type="checkbox" checked={selected} disabled={primary || (inactive && !selected)}
               onChange={e => e.target.checked
                 ? onChange({ ...value, serviceSubcategoryIds: [...value.serviceSubcategoryIds, sub.serviceSubcategoryId] })
                 : remove(sub.serviceSubcategoryId)} />
             <span>{sub.name}{primary ? " (primary)" : ""}{inactive ? " (inactive)" : ""}</span>
           </label>;
         })}
       </fieldset>)}
     </div>
     <p className="field-hint">Select as many as apply. Change the primary subcategory using the field above.</p>
   </div>}
   <div className="service-classification-chips">
    {value.serviceSubcategoryIds.map(id => {
          const sub = allSubcategories.find(s => s.serviceSubcategoryId === id);
          return <button className="service-classification-chip" type="button" key={id} onClick={() => remove(id)} aria-label={`Remove ${sub?.name ?? "subcategory"}`}>
      {sub ? `${sub.category.name} · ${sub.name}` : `Existing subcategory ${id}`}{id === value.primaryServiceSubcategoryId ? " · Primary" : ""} ×
     </button>;
        })}
   </div>
  </div>
 </>;
}
